import Axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";
import { toVault77ApiError } from "../../core/apiError";
import { PAY_RATE_HISTORY_ENDPOINT } from "./constants";
import type {
    PayRateHistory,
    PayRateHistoryFilters,
    CreatePayRateHistoryRequest,
    UpdatePayRateHistoryRequest,
} from "../types";

/**
 * Fetch pay rate history records with optional filters.
 * GET api/vault77/payRateHistory
 */
export async function getPayRateHistoryList(
    filters?: PayRateHistoryFilters
): Promise<PayRateHistory[]> {
    try {
        let url = PAY_RATE_HISTORY_ENDPOINT;
        if (filters) {
            const params = new URLSearchParams();
            if (filters.employeeId != null) params.append("employeeId", String(filters.employeeId));
            if (filters.departmentId != null) params.append("departmentId", String(filters.departmentId));
            if (filters.startDate != null) params.append("startDate", filters.startDate);
            if (filters.endDate != null) params.append("endDate", filters.endDate);
            const query = params.toString();
            if (query) url = `${url}?${query}`;
        }
        const response = await Axios.get<PayRateHistory[]>(url, AxiosConfig.configValue("min"));
        return response.data;
    } catch (error) {
        console.error("getPayRateHistoryList/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch pay rate history.");
    }
}

/**
 * Fetch a single pay rate history record by ID.
 * GET api/vault77/payRateHistory/{id}
 */
export async function getPayRateHistoryById(id: number): Promise<PayRateHistory> {
    try {
        const response = await Axios.get<PayRateHistory>(
            `${PAY_RATE_HISTORY_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("getPayRateHistoryById/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch pay rate history record.");
    }
}

/**
 * Fetch the current effective pay rate for an employee.
 * GET api/vault77/payRateHistory/current?employeeId={employeeId}
 * Returns null when the employee has no pay rate on record (API responds 404).
 */
export async function getCurrentPayRate(employeeId: number): Promise<PayRateHistory | null> {
    try {
        const response = await Axios.get<PayRateHistory>(
            `${PAY_RATE_HISTORY_ENDPOINT}/current?employeeId=${employeeId}`,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        // No pay rate on record → API returns a bare 404; treat as "no current rate", not an error.
        // Duck-type the status (axios 0.19.x has no isAxiosError helper).
        const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
        if (status === 404) {
            return null;
        }
        console.error("getCurrentPayRate/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch the current pay rate.");
    }
}

export async function isEmployeeInPayRateScope(employeeId: number): Promise<boolean> {
    try {
        const response = await Axios.get<{ inScope: boolean }>(
            `${PAY_RATE_HISTORY_ENDPOINT}/inScope?employeeId=${employeeId}`,
            AxiosConfig.configValue("min")
        );
        return response.data.inScope;
    } catch (error) {
        console.error("isEmployeeInPayRateScope/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to verify pay rate access.");
    }
}

/**
 * Create a new pay rate history record.
 * POST api/vault77/payRateHistory
 * The API auto-supersedes the prior open rate for the employee.
 */
export async function createPayRateHistory(
    request: CreatePayRateHistoryRequest
): Promise<PayRateHistory> {
    try {
        const response = await Axios.post<PayRateHistory>(
            PAY_RATE_HISTORY_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("createPayRateHistory/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to create pay rate history record.");
    }
}

/**
 * Create a pay rate history record from an existing employee review.
 * POST api/vault77/payRateHistory/applyReview/{reviewId}
 * Body-less: a null body lets the browser send Content-Length: 0 (the API requires it).
 */
export async function applyReviewToPayRate(reviewId: number): Promise<PayRateHistory> {
    try {
        const response = await Axios.post<PayRateHistory>(
            `${PAY_RATE_HISTORY_ENDPOINT}/applyReview/${reviewId}`,
            null,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("applyReviewToPayRate/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to create a pay rate from the review.");
    }
}

/**
 * Update an existing pay rate history record.
 * PUT api/vault77/payRateHistory (ID is in the request body, not the URL)
 */
export async function updatePayRateHistory(
    request: UpdatePayRateHistoryRequest
): Promise<PayRateHistory> {
    try {
        const response = await Axios.put<PayRateHistory>(
            PAY_RATE_HISTORY_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("updatePayRateHistory/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to update pay rate history record.");
    }
}

/**
 * Delete a pay rate history record by ID (soft delete).
 * DELETE api/vault77/payRateHistory/{id}
 */
export async function deletePayRateHistory(id: number): Promise<void> {
    try {
        await Axios.delete(
            `${PAY_RATE_HISTORY_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
    } catch (error) {
        console.error("deletePayRateHistory/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to delete pay rate history record.");
    }
}
